import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Spin, message, Modal } from 'antd';
import { useQuery, useMutation, gql } from '@apollo/client';

import ErrorBoundary from '../../components/Utils/ErrorBoundary';

import Document from '../Document/index';
import { EmployeeDetails } from '../../components/Employee';
import { EMPLOYEE_ALL_FIELDS } from '../../apollo/fragments';
import {
  DESIGNATIONS_OPTION_QUERY,
  LOCATIONS_OPTIONS_QUERY,
  PORTFOLIO_OPTIONS_QUERY,
  PRODUCT_TYPE_OPTIONS_QUERY,
  STAGE_OPTIONS_QUERY,
} from '../../apollo/queries';
import {
  employeeRelieved,
  employeeRejoined,
  updateGridTabTitle,
} from '../../store/reducers/employeeSlice';

import * as Config from '../../appConfig';
import './styles.css';

var fileDownload = require('js-file-download');

const EMPLOYEE_VIEW_QUERY = gql`
  ${EMPLOYEE_ALL_FIELDS}
  query EmployeeById($id: ID!) {
    employeeById(id: $id) {
      ...EmployeeFields
    }
  }
`;

const EMPLOYEE_UPDATE_BASICINFO_QUERY = gql`
  mutation UpdateBasicInfo($input: BasicInfoInput) {
    updateBasicInfo(input: $input) {
      id
      first_name
    }
  }
`;

const EMPLOYEE_UPDATE_IDCARD_QUERY = gql`
  mutation updateIdCard($id: ID!, $input: IdCardInput) {
    updateIdCard(id: $id, input: $input) {
      id
      employee_id
      card_number
      card_validity_end
      dra_number
      dra_training_start
      dra_training_end
      dra_status
      pvc_number
      pvc_validity_end
      rac_mobility_id
      cc_mobility_id
      did_number
    }
  }
`;

const EMPLOYEE_UPDATE_HEALTH_INSURANCE_QUERY = gql`
  mutation updateHealthInsurance($id: ID!, $input: HealthInsuranceInput) {
    updateHealthInsurance(id: $id, input: $input) {
      id
      provider
      policy_start_date
      policy_end_date
      type
      sum_insured
    }
  }
`;

const EMPLOYEE_UPDATE_BANKINFO_QUERY = gql`
  mutation updateBankInfo($id: ID!, $input: BankInfoInput) {
    updateBankInfo(id: $id, input: $input) {
      id
      employee {
        id
        first_name
      }
    }
  }
`;

const EMPLOYEE_UPDATE_CONTACT_QUERY = gql`
  mutation updateContact($id: ID!, $input: ContactInput) {
    updateContact(id: $id, input: $input) {
      id
      employee {
        id
        first_name
      }
    }
  }
`;

const EMPLOYEE_RELIEVE_QUERY = gql`
  mutation relieveEmployee($id: ID!, $input: RelieveEmployeeInput!) {
    relieveEmployee(id: $id, input: $input) {
      id
      status
      date_of_relieving
    }
  }
`;

const EMPLOYEE_REJOIN_QUERY = gql`
  mutation rejoinEmployee($id: ID!, $input: RejoinEmployeeInput!) {
    rejoinEmployee(id: $id, input: $input) {
      id
      status
      date_of_joining
    }
  }
`;

const EMPLOYEE_ID_RESET_QUERY = gql`
  mutation regenerateEmployeeId($id: ID!) {
    regenerateEmployeeId(id: $id) {
      id
      emp_id
    }
  }
`;

const DOCUMENT_DELETE_QUERY = gql`
  mutation deleteDcoument($id: ID!) {
    deleteDcoument(id: $id) {
      id
      media {
        file_name
      }
    }
  }
`;

// const EMPLOYEE_UPLOAD_FILE_QUERY = gql`
//   mutation Upload($file: Upload!) {
//     upload(file: $file)
//   }
// `;

const EmployeeView = ({ employee }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.login);
  // const [documentModalVisible, setDocumentModalVisible] = useState(false);
  // const [lastDocumentAdded, setLastDocumentAdded] = useState(null);
  const [documentBlob, setDocumentBlob] = useState(null);
  const [documentName, setDocumentName] = useState('');
  const [documentRendering, setDocumentRendering] = useState(false);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);

  const { loading, data, refetch } = useQuery(EMPLOYEE_VIEW_QUERY, {
    variables: {
      id: employee.id,
    },
  });
  const { loading: loadingD, data: designations } = useQuery(
    DESIGNATIONS_OPTION_QUERY
  );
  const { loading: loadingR, data: locations } = useQuery(
    LOCATIONS_OPTIONS_QUERY,
    {
      variables: {
        userId: user.id,
      },
    }
  );
  const { loading: loadingP, data: portfolios } = useQuery(
    PORTFOLIO_OPTIONS_QUERY
  );
  const { loading: loadingPoductType, data: productTypes } = useQuery(
    PRODUCT_TYPE_OPTIONS_QUERY
  );
  const { loading: loadingStage, data: stages } = useQuery(STAGE_OPTIONS_QUERY);

  const onSuccessMutation = (mesageText = 'Details updated successfully!') => {
    refetch({
      variables: {
        id: employee.id,
      },
    });
    message.success(mesageText);
  };

  const [updateBasicInfo, { loading: doingUpdateBasicInfo }] = useMutation(
    EMPLOYEE_UPDATE_BASICINFO_QUERY
  );

  const [updateIdCard, { loading: doingUpdateIdCard }] = useMutation(
    EMPLOYEE_UPDATE_IDCARD_QUERY
  );

  const [
    updateHealthInsurance,
    { loading: doingUpdateHealthInsurance },
  ] = useMutation(EMPLOYEE_UPDATE_HEALTH_INSURANCE_QUERY);

  const [updateBankInfo, { loading: doingUpdateBankInfo }] = useMutation(
    EMPLOYEE_UPDATE_BANKINFO_QUERY
  );

  const [updateContact, { loading: doingUpdateContact }] = useMutation(
    EMPLOYEE_UPDATE_CONTACT_QUERY
  );

  const [relieveEmployee, { loading: doingRelieveEmployee }] = useMutation(
    EMPLOYEE_RELIEVE_QUERY
  );

  const [rejoinEmployee, { loading: doingRejoinEmployee }] = useMutation(
    EMPLOYEE_REJOIN_QUERY
  );

  const [regenerateEmployeeId, { loading: doingResetEmployeeId }] = useMutation(
    EMPLOYEE_ID_RESET_QUERY
  );

  const [deleteDcoument, { loading: doingDeleteDcoument }] = useMutation(
    DOCUMENT_DELETE_QUERY
  );

  const onBasicInfoUpdate = (values, callBack) => {
    console.log('onBasicInfoUpdate triggered with : ', values);

    updateBasicInfo({
      variables: {
        input: {
          id: values.id,
          first_name: values.first_name,
          last_name: values.last_name,
          location_id: values.location && parseInt(values.location[1]),
          designation_id: parseInt(values.designation),
          portfolio_id: parseInt(values.portfolio),
          product_type_id: parseInt(values.productType),
          stage_id: parseInt(values.stage),
          date_of_joining: values.date_of_joining.format(
            Config.DATE_FORMAT_SERVER
          ),
          aadhar: values.aadhar,
          pan: values.pan,
        },
      },
    })
      .then(({ data: { updateBasicInfo: basicInfo } }) => {
        console.log('onBasicInfoUpdate done', basicInfo);
        if (basicInfo) {
          onSuccessMutation();
        }
      })
      .catch((e) => {
        console.log('mutationError ////', e, e.graphQLErrors);
        let error = '';
        if (e.graphQLErrors) {
          e.graphQLErrors.forEach(({ extensions, message }) => {
            const { category, validation } = extensions;
            if (category === 'validation') {
              error = Object.keys(validation).map((field) => {
                return (
                  <span>{validation[field][0].replace('input.', '')}</span>
                );
              });
              if (callBack) {
                callBack({
                  type: 'validation',
                  message: error,
                  field: 'aadhar',
                });
              }
            } else {
              error = message;
            }
          });
        }
        message.error(
          error ? error : 'Some error occured, Please try again !!!'
        );
      });
  };

  const onIdCardInfoUpdate = (values) => {
    console.log('onIdCardInfoUpdate triggered with : ', values);
    updateIdCard({
      variables: {
        id: values.id,
        input: {
          card_number: values.card_number,
          card_validity_end:
            values.card_number && values.card_validity_end
              ? values.card_validity_end.format(Config.DATE_FORMAT_SERVER)
              : null,
          dra_number: values.dra_number,
          dra_training_start:
            values.dra_number &&
            values.dra_training_period &&
            values.dra_training_period[0]
              ? values.dra_training_period[0].format(Config.DATE_FORMAT_SERVER)
              : null,
          dra_training_end:
            values.dra_number &&
            values.dra_training_period &&
            values.dra_training_period[1]
              ? values.dra_training_period[1].format(Config.DATE_FORMAT_SERVER)
              : null,
          dra_status: values.dra_status,
          pvc_number: values.pvc_number,
          pvc_validity_end:
            values.pvc_number && values.pvc_validity_end
              ? values.pvc_validity_end.format(Config.DATE_FORMAT_SERVER)
              : null,
          rac_mobility_id: values.rac_mobility_id,
          cc_mobility_id: values.cc_mobility_id,
          did_number: values.did_number,
        },
      },
    })
      .then(({ data: { updateIdCard: idCard } }) => {
        console.log('onIdCardInfoUpdate finished', idCard);
        if (idCard) {
          onSuccessMutation();
        }
      })
      .catch((e) => {
        console.log('mutationError ////', e, e.graphQLErrors);
        let error = 'Some error occured, Please try again !!!';
        message.error(error);
      });
  };

  const onHealthInsuranceUpdate = (values) => {
    console.log('onHealthInsuranceUpdate triggered with : ', values);
    updateHealthInsurance({
      variables: {
        id: values.id,
        input: {
          provider: values.provider,
          policy_start_date:
            values.policy_period && values.policy_period[0]
              ? values.policy_period[0].format(Config.DATE_FORMAT_SERVER)
              : null,
          policy_end_date:
            values.policy_period && values.policy_period[1]
              ? values.policy_period[1].format(Config.DATE_FORMAT_SERVER)
              : null,
          type: values.type,
          sum_insured: values.sum_insured ? values.sum_insured : '',
        },
      },
    })
      .then(({ data: { updateHealthInsurance: healthInsurance } }) => {
        console.log('onHealthInsuranceUpdate finished', healthInsurance);
        if (healthInsurance) {
          onSuccessMutation();
        }
      })
      .catch((e) => {
        console.log('mutationError ////', e, e.graphQLErrors);
        let error = 'Some error occured, Please try again !!!';
        message.error(error);
      });
  };

  const onBankInfoUpdate = (values, callBack) => {
    console.log('onBankInfoUpdate triggered with : ', values);
    updateBankInfo({
      variables: {
        id: values.id,
        input: {
          bank_name: values.bank_name,
          account_number: values.account_number,
          branch: values.branch,
          ifsc_code: values.ifsc_code,
        },
      },
    })
      .then(({ data: { updateBankInfo: bankInfo } }) => {
        console.log('onBankInfoUpdate done', bankInfo);
        if (bankInfo) {
          onSuccessMutation();
        }
      })
      .catch((e) => {
        console.log('mutationError ////', e, e.graphQLErrors);
        let error = 'Some error occured, Please try again !!!';
        message.error(error);
      });
  };

  const onContactInfoUpdate = (values) => {
    console.log('onContactInfoUpdate triggered with : ', values);
    updateContact({
      variables: {
        id: values.id,
        input: {
          phone_1: values.phone_1,
          phone_2: values.phone_2,
          email: values.email,
          address_1: values.address_1,
          address_2: values.address_2,
          city: values.city,
          state: values.state,
          pin_code: values.pin_code,
          reference_name: values.reference_name,
          reference_contact: values.reference_contact,
        },
      },
    })
      .then(({ data: { updateContact: contactInfo } }) => {
        console.log('onContactInfoUpdate done', contactInfo);
        if (contactInfo) {
          onSuccessMutation();
        }
      })
      .catch((e) => {
        console.log('mutationError ////', e, e.graphQLErrors);
        let error = 'Some error occured, Please try again !!!';
        message.error(error);
      });
  };

  const onDocumentAdd = (document) => {
    console.log('onDocumentAdd triggered with : ', document);
    // setDocumentModalVisible(false);
    onSuccessMutation('Document added successfully!');
  };

  const onDocumentUpdate = (values) => {
    console.log('onDocumentUpdate triggered with : ', values);
  };

  const onDocumentDelete = (values) => {
    console.log('onDocumentDelete triggered with : ', values);
    deleteDcoument({
      variables: {
        id: values.id,
      },
    })
      .then(({ data: { deleteDcoument: document } }) => {
        console.log('onDocumentDelete done', document);
        if (document) {
          onSuccessMutation('Document deleted successfully!');
        }
      })
      .catch((e) => {
        console.log('mutationError ////', e, e.graphQLErrors);
        let error = 'Some error occured, Please try again !!!';
        message.error(error);
      });
  };
  const onRelieveEmployeeSubmit = (values, callBack) => {
    console.log('onRelieveEmployeeSubmit triggered with : ', values);
    relieveEmployee({
      variables: {
        id: values.id,
        input: {
          date_of_relieving: values.date_of_relieving.format(
            Config.DATE_FORMAT_SERVER
          ),
          relieving_note: values.reason,
        },
      },
    })
      .then(({ data: { relieveEmployee: employee } }) => {
        console.log('onRelieveEmployeeSubmit done', employee);
        if (employee) {
          if (callBack) {
            callBack(employee);
          }
          dispatch(employeeRelieved({ employee }));
          onSuccessMutation('Employee relieve status updated!');
        }
      })
      .catch((e) => {
        console.log('mutationError ////', e, e.graphQLErrors);
        let error = 'Some error occured, Please try again !!!';
        message.error(error);
      });
  };

  const onRejoinEmployeeSubmit = (values, callBack) => {
    console.log('onRejoinEmployeeSubmit triggered with : ', values);
    rejoinEmployee({
      variables: {
        id: values.id,
        input: {
          date_of_joining: values.date_of_relieving.format(
            Config.DATE_FORMAT_SERVER
          ),
        },
      },
    })
      .then(({ data: { rejoinEmployee: employee } }) => {
        console.log('onRejoinEmployeeSubmit done', employee);
        if (employee) {
          if (callBack) {
            callBack(employee);
          }
          dispatch(employeeRejoined({ employee }));
          onSuccessMutation('Employee rejoin status updated!');
        }
      })
      .catch((e) => {
        console.log('mutationError ////', e, e.graphQLErrors);
        let error = 'Some error occured, Please try again !!!';
        message.error(error);
      });
  };

  const onResetEmployeeIdSubmit = (values, callBack) => {
    console.log('onResetEmployeeIdSubmit triggered with : ', values);
    regenerateEmployeeId({
      variables: {
        id: values.id,
      },
    })
      .then(({ data: { regenerateEmployeeId: employee } }) => {
        console.log('onResetEmployeeIdSubmit done', employee);
        if (employee) {
          if (callBack) {
            callBack(employee);
          }
          dispatch(
            updateGridTabTitle({
              title: employee.emp_id,
              key: `empTab-${employee.id}`,
            })
          );
          onSuccessMutation('Employee ID updated!');
        }
      })
      .catch((e) => {
        console.log('mutationError ////', e, e.graphQLErrors);
        let error = 'Some error occured, Please try again !!!';
        message.error(error);
      });
  };

  // const onAddOrUpdateDocumentClick = (values) => {
  //   console.log('onAddOrUpdateDocumentClick triggered with : ', values);
  //   setDocumentModalVisible(true);
  // };

  const onDocumentDownload = (values) => {
    console.log('onDocumentDownload triggered with : ', values);
    const {
      id: docId,
      media: [mediaItem],
    } = values;
    if (docId && mediaItem && mediaItem.file_name) {
      fetch(
        `${process.env.REACT_APP_API_BASE_URL}/employees/document/download`,
        {
          method: 'POST',
          cache: 'no-cache',
          headers: new Headers({
            Authorization: 'Bearer ' + localStorage.getItem('token'),
          }),
          body: JSON.stringify({
            docId: values.id,
          }),
        }
      )
        .then((response) => {
          if (response.status === 200) {
            return response.blob();
          } else {
            throw new Error('Error');
          }
        })
        .then((blob) => {
          fileDownload(blob, mediaItem.file_name);
        })
        .catch((e) => {
          message.error('Something went wrong, Please try again!');
        });
    } else {
      message.error('Requested file not found!');
    }
  };

  const blobToBase64 = async (blob) => {
    return new Promise((resolve, _) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  };

  const onDocumentView = (values) => {
    console.log('onDocumentView triggered with : ', values);
    const {
      id: docId,
      media: [mediaItem],
    } = values;
    if (docId && mediaItem && mediaItem.file_name) {
      setDocumentRendering(true);
      fetch(
        `${process.env.REACT_APP_API_BASE_URL}/employees/document/download`,
        {
          method: 'POST',
          cache: 'no-cache',
          headers: new Headers({
            Authorization: 'Bearer ' + localStorage.getItem('token'),
          }),
          body: JSON.stringify({
            docId: values.id,
          }),
        }
      )
        .then((response) => {
          if (response.status === 200) {
            return response.blob();
          } else {
            throw new Error('Error');
          }
        })
        .then(async (blob) => {
          const base64 = await blobToBase64(blob);
          console.log({ base64 });
          setDocumentBlob(base64);
          setDocumentName(mediaItem.file_name);
          setShowDocumentViewer(true);
          setDocumentRendering(false);
        })
        .catch((e) => {
          message.error('Something went wrong, Please try again!');
          setDocumentRendering(false);
        });
    } else {
      message.error('Requested file not found!');
    }
  };

  const EmployeeDetailsProps = {
    designations: designations ? designations.designations : [],
    locations: locations ? locations.locations : [],
    portfolios: portfolios ? portfolios.portfolios : [],
    productTypes: productTypes ? productTypes.productTypes : [],
    stages: stages ? stages.stages : [],
    onBasicInfoUpdate: onBasicInfoUpdate,
    onIdCardInfoUpdate: onIdCardInfoUpdate,
    onHealthInsuranceUpdate: onHealthInsuranceUpdate,
    onBankInfoUpdate: onBankInfoUpdate,
    onContactInfoUpdate: onContactInfoUpdate,
    onDocumentAdd: onDocumentAdd,
    onDocumentUpdate: onDocumentUpdate,
    onDocumentDelete: onDocumentDelete,
    onRelieveEmployeeSubmit: onRelieveEmployeeSubmit,
    onRejoinEmployeeSubmit: onRejoinEmployeeSubmit,
    onResetEmployeeIdSubmit: onResetEmployeeIdSubmit,
    // onFileUpload: onFileUpload
    onDocumentDownload: onDocumentDownload,
    onDocumentView: onDocumentView,
    uploadDocument: (
      <Document
        entityId={employee.id}
        entityType="employee"
        formId="myForm"
        noSubmitButtons={true}
        onDocumentAdd={onDocumentAdd}
      />
    ),
    ability: user.ability,
  };

  return (
    <ErrorBoundary>
      <Spin
        spinning={
          loading ||
          loadingD ||
          loadingR ||
          loadingP ||
          loadingPoductType ||
          loadingStage ||
          doingUpdateBasicInfo ||
          doingUpdateIdCard ||
          doingUpdateHealthInsurance ||
          doingUpdateBankInfo ||
          doingUpdateContact ||
          doingDeleteDcoument ||
          doingRelieveEmployee ||
          doingResetEmployeeId ||
          doingRejoinEmployee ||
          documentRendering
        }
      >
        {/* TODO: Below code only for force child rerender, need to find better way  */}
        {employee && !data && (
          <EmployeeDetails employee={employee} {...EmployeeDetailsProps} />
        )}
        {data && data.employeeById && (
          <EmployeeDetails
            employee={data.employeeById}
            {...EmployeeDetailsProps}
          />
        )}
      </Spin>
      {/* <Modal
        title="Add new document"
        maskClosable={false}
        visible={documentModalVisible}
        confirmLoading={false}
        destroyOnClose={true}
        footer={[
          <Button
            type="secondary"
            onClick={() => setDocumentModalVisible(false)}
          >
            Cancel
          </Button>,
          <Button form="myForm" key="submit" type="primary" htmlType="submit">
            Submit
          </Button>,
        ]}
      >
        <Document
          entityId={employee.id}
          entityType="employee"
          formId="myForm"
          noSubmitButtons={true}
          onDocumentAdd={onDocumentAdd}
        />
      </Modal> */}
      {showDocumentViewer && (
        <Modal
          title={documentName}
          visible={showDocumentViewer}
          confirmLoading={false}
          destroyOnClose={true}
          centered
          style={{ maxWidth: '1200px' }}
          width="90%"
          footer={null}
          onCancel={() => setShowDocumentViewer(false)}
        >
          <object
            data={documentBlob}
            type="application/pdf"
            width="100%"
            height="600"
          >
            <p>
              If document is not displayed properly then please document and
              view it on your local machine.
            </p>
          </object>
        </Modal>
      )}
    </ErrorBoundary>
  );
};

EmployeeView.defaultProps = {};

EmployeeView.propTypes = {
  employee: PropTypes.object.isRequired,
};

export default EmployeeView;
